"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.file_directInsertFinancialCovenants = void 0;
const _ = __importStar(require("lodash"));
const zod_1 = require("zod");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const tape_builder_base_1 = require("../../../types/tape-builder-base");
const commonTapeNode_1 = require("../../commonTapeNode");
const utils = __importStar(require("../../utils"));
const utils_1 = require("../../utils");
const types_1 = require("./types");
const mapZodTypeName = (typeName) => {
    if (typeName instanceof zod_1.z.ZodString) {
        return 'string';
    }
    if (typeName instanceof zod_1.z.ZodNumber) {
        return 'float';
    }
    return (0, common_1.finleyAssertUnreachable)(typeName, `mapZodTypeName: typeName is not a ZodString or ZodNumber, found: ${typeName}`);
};
const { isNode, isIntermediateRecord, ...otherProps } = (0, commonTapeNode_1.commonTapeNodeUtils)(types_1.nodeType, types_1.artifactType, types_1.UIConfigSchema);
// create a new UI node of this type.
const createNewNode = ({ id, suggestedSerialNumber, nodeToCopy, }) => {
    const friendlyName = `Direct Insert Financial Covenants`;
    const nodeData = {
        metadata: {
            tapeBuilderNodeId: id,
            tapeBuilderNodeType: types_1.nodeType,
        },
        configuration: {
            artifactSlug: `${_.camelCase(friendlyName)}_${suggestedSerialNumber}`,
            creditFacilitySlug: 'CREDIT_FACILITY_SLUG',
            financialCovenantsArtifactSlug: (0, tape_builder_base_1.asArtifactSlugRef)('PLACEHOLDER'),
            format: 'columnPerEntry',
            financialCovenantKeys: ['PLACEHOLDER'],
        },
    };
    if (nodeToCopy) {
        (0, common_1.finleyAssert)(isNode(nodeToCopy), `node must be a ${types_1.nodeType} node`);
        nodeData.configuration = {
            ..._.cloneDeep(nodeToCopy.configuration),
            artifactSlug: utils.updateArtifactSlug(nodeData.configuration.artifactSlug, suggestedSerialNumber),
            financialCovenantsArtifactSlug: (0, tape_builder_base_1.asArtifactSlugRef)('PLACEHOLDER'),
        };
    }
    return nodeData;
};
/*
 * validate the configuration for the node. This checks shape as well
 * logical correctness.
 * This function should validate the config wrt rest of the graph as well.
 * if there is an error it should return an array of error messages.
 * if there are no errors it should return an empty array.
 */
const hasValidConfig = ({ graph, node, getUINode, }) => {
    (0, common_1.finleyAssert)(isNode(node), `node must be a ${types_1.nodeType} node`);
    const edgeErrors = (0, utils_1.checkEdges)({
        node,
        graph,
        isAllowedInputNode: () => true,
        getUINode,
        minInputs: 1,
        maxInputs: 100,
        minOutputs: 0,
        maxOutputs: 100,
    });
    return edgeErrors;
};
// validate given IntermediateRecord.
// this is called after initial Config validation is done
// and the intermediate record is generated.
// here an instance of ITapeBuilder is available, which can be used
// to inspect any Tape definitions.
const validateIntermediateTape = ({ tape, graph, tapeBuilder, }) => {
    (0, common_1.finleyAssert)(isIntermediateRecord(tape), `validateIntermediateTape: tape is not an ${types_1.artifactType}`);
    (0, common_1.finleyAssert)(tape.type === 'directInsertFinancialCovenants', `node is not a directInsertFinancialCovenants, found: [tape.type:${tape.type}]`);
    const { id, artifactSlug, configuration: { financialCovenantsArtifactSlug }, } = tape;
    const artifactInputs = utils.getDirectInputsForArtifact({
        targetArtifactId: id,
        ...graph,
    });
    const configErrors = [];
    // mutates local configErrors
    const artifactInput = artifactInputs.find((input) => input.artifactSlug === financialCovenantsArtifactSlug);
    if (!artifactInput) {
        configErrors.push(`directInsertFinancialCovenants: [${artifactSlug}] cannot find [inputArtifact:${financialCovenantsArtifactSlug}] listed as an input dependency.`);
    }
    else {
        const tapeDefinitionResult = tapeBuilder.generateTapeDefinition(artifactInput.id);
        if (!tapeDefinitionResult.isValid || !tapeDefinitionResult.data) {
            configErrors.push(`directInsertFinancialCovenants: [${artifactSlug}] cannot get tape definition of [inputArtifact:${financialCovenantsArtifactSlug}].`);
        }
        else {
            const baseFields = tapeDefinitionResult.data.baseFields;
            if (tape.configuration.format === 'columnPerEntry') {
                // Column per entry - make sure each column we're pulling from is
                // present and has the right type.
                const valueType = mapZodTypeName(finley_types_1.FinancialCovenant.FinancialCovenantDirectInsertRecordStruct.shape
                    .value);
                tape.configuration.financialCovenantKeys.forEach((key) => {
                    if (!baseFields.some((bf) => bf.field === key && bf.type === valueType)) {
                        configErrors.push(`directInsertFinancialCovenants: [${artifactSlug}] [inputArtifact:${financialCovenantsArtifactSlug}] expected to have [field:${key}] of [type:${valueType}]`);
                    }
                });
            }
            else if (tape.configuration.format === 'rowPerEntry') {
                // Row per entry - each row should adhere to the expected format.
                Object.entries(finley_types_1.FinancialCovenant.FinancialCovenantDirectInsertRecordStruct.shape)
                    .map(([key, val]) => ({
                    fieldName: key,
                    type: mapZodTypeName(val),
                }))
                    .forEach((field) => {
                    const foundField = baseFields.find((baseField) => baseField.field === field.fieldName);
                    if (!foundField) {
                        configErrors.push(`directInsertFinancialCovenants: [${artifactSlug}] [inputArtifact:${financialCovenantsArtifactSlug}] tape definition is missing [field:${field.fieldName}].`);
                    }
                    else if (foundField.type !== field.type) {
                        configErrors.push(`directInsertFinancialCovenants: [${artifactSlug}] [inputArtifact:${financialCovenantsArtifactSlug}] tape definition for [fieldName:${field.fieldName}] should be [type:${field.type}], but got [type:${foundField.type}].`);
                    }
                });
            }
            else {
                (0, common_1.finleyAssertUnreachable)(tape.configuration.format);
            }
        }
    }
    return configErrors;
};
/**
 * Represents a UI node with various properties and methods.
 */
exports.file_directInsertFinancialCovenants = {
    // what should node say on the tape-builder node creation menu.
    nodeTypeText: 'Direct Insert FinCovs',
    // what is the node type. must be one of the constants defined in TapeBuilderNodeType
    nodeType: types_1.nodeType,
    // expose artifactType
    artifactType: types_1.artifactType,
    isOperation: false,
    isRaw: false,
    isFinal: true,
    // operation nodes dont render badges.
    badgeTexts: ['DirectInsert'],
    // creates sample configuration for this node.
    createNewNode,
    // typeguard that checks if the node is of this type.
    isNode,
    isIntermediateRecord,
    ...otherProps,
    // validate the configuration for the node for logical correctness
    hasValidConfig,
    // validate generated config
    validateIntermediateTape,
};
//# sourceMappingURL=uinode.js.map